import { useState, useCallback, useMemo } from "react";

/**
 * Estados disponibles para filtros de TAREAS
 */
export const ESTADOS_TAREA_CLIENTE = [
  { value: "PENDIENTE", label: "Pendiente", color: "warning" },
  { value: "EN PROCESO", label: "En Proceso", color: "info" },
  { value: "TERMINADA", label: "Terminada", color: "success" },
];

/**
 * Estados disponibles para filtros de FACTURAS
 * Los labels reflejan la terminología del cliente, no los valores internos de BD.
 */
export const ESTADOS_FACTURA_CLIENTE = [
  { value: "FACTURADO", label: "Pendiente", color: "warning" },
  { value: "ABONO", label: "Pago parcial", color: "secondary" },
  { value: "PAGADO", label: "Pagada", color: "success" },
];

/**
 * Tipos de tarea disponibles
 */
export const TIPOS_TAREA = [
  { value: "MES", label: "Mensual" },
  { value: "AÑO", label: "Anual" },
  { value: "ESPECIAL", label: "Especial" },
];

/**
 * Filtros iniciales para TAREAS
 */
const INITIAL_FILTERS_TAREAS = {
  busqueda: "",
  estados: [],
  tipoTarea: "",
  fechaInicioDesde: null,
  fechaInicioHasta: null,
};

/**
 * Filtros iniciales para FACTURAS
 */
const INITIAL_FILTERS_FACTURAS = {
  busqueda: "",
  estados: [],
  fechaFacturacionDesde: null,
  fechaFacturacionHasta: null,
  montoMin: "",
  montoMax: "",
};

/**
 * Hook personalizado para manejo de filtros en portal de clientes
 *
 * @param {string} tipo - Tipo de filtro: "tareas" | "facturas"
 * @returns {object} Objeto con estado y funciones de filtros
 *
 * @example Para tareas
 * const {
 *   filtros,
 *   updateFiltro,
 *   toggleEstado,
 *   limpiarFiltros,
 *   removerFiltro,
 *   filtrosActivos,
 *   hayFiltrosActivos
 * } = useClienteFilters("tareas");
 *
 * @example Para facturas
 * const {
 *   filtros,
 *   updateFiltro,
 *   toggleEstado,
 *   limpiarFiltros,
 *   removerFiltro,
 *   filtrosActivos,
 *   hayFiltrosActivos
 * } = useClienteFilters("facturas");
 */
export function useClienteFilters(tipo = "tareas") {
  // Determinar filtros iniciales según el tipo
  const initialFilters =
    tipo === "facturas" ? INITIAL_FILTERS_FACTURAS : INITIAL_FILTERS_TAREAS;

  const [filtros, setFiltros] = useState(initialFilters);

  /**
   * Actualiza un filtro específico
   */
  const updateFiltro = useCallback((campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  }, []);

  /**
   * Toggle de estado en array de estados
   * Si el estado ya está seleccionado, lo remueve
   * Si no está, lo agrega
   */
  const toggleEstado = useCallback((estado) => {
    setFiltros((prev) => {
      const estadosActuales = prev.estados || [];
      return {
        ...prev,
        estados: estadosActuales.includes(estado)
          ? estadosActuales.filter((e) => e !== estado)
          : [...estadosActuales, estado],
      };
    });
  }, []);

  /**
   * Limpia todos los filtros
   */
  const limpiarFiltros = useCallback(() => {
    setFiltros(initialFilters);
  }, [initialFilters]);

  /**
   * Remueve un filtro específico
   * @param {string} campo - Campo del filtro a remover
   * @param {string} valor - Valor específico (para arrays como estados)
   */
  const removerFiltro = useCallback(
    (campo, valor = null) => {
      setFiltros((prev) => {
        // Si es un array de estados y se proporciona un valor específico
        if (campo === "estados" && valor) {
          return {
            ...prev,
            estados: prev.estados.filter((e) => e !== valor),
          };
        }

        // Para otros campos, resetear al valor inicial
        return {
          ...prev,
          [campo]: initialFilters[campo],
        };
      });
    },
    [initialFilters],
  );

  /**
   * Cuenta filtros activos
   */
  const filtrosActivos = useMemo(() => {
    let count = 0;

    // Búsqueda
    if (filtros.busqueda) count++;

    // Estados (cada estado cuenta como un filtro)
    if (filtros.estados?.length > 0) count += filtros.estados.length;

    // Tipo de tarea (solo para tareas)
    if (tipo === "tareas" && filtros.tipoTarea) count++;

    // Fechas de inicio (solo para tareas)
    if (tipo === "tareas") {
      if (filtros.fechaInicioDesde) count++;
      if (filtros.fechaInicioHasta) count++;
    }

    // Fechas de facturación (solo para facturas)
    if (tipo === "facturas") {
      if (filtros.fechaFacturacionDesde) count++;
      if (filtros.fechaFacturacionHasta) count++;
      if (filtros.montoMin) count++;
      if (filtros.montoMax) count++;
    }

    return count;
  }, [filtros, tipo]);

  /**
   * Verifica si hay filtros activos
   */
  const hayFiltrosActivos = filtrosActivos > 0;

  return {
    // Estado
    filtros,
    filtrosActivos,
    hayFiltrosActivos,

    // Acciones
    updateFiltro,
    toggleEstado,
    limpiarFiltros,
    removerFiltro,
  };
}
